// ============================================================================
// LIT-CASE — Case Details section (ADR-0001)
// ============================================================================
// Three editable cards composed from existing backends:
//   1. Case Details   — filing date / filed amount / subtype / court case # +
//                       Case Brief (litigationCaseService.updateMeta / updateNotes)
//   2. Court / Arbitrator — court or arbitrator, place, reference #, etc.
//                       (caseCourtDetailService.save; per-type labels)
//   3. Our Advocate   — advocate from master + contact (caseAdvocateService)
//
// System Case Number = internal case_code (read-only, per ADR-0001 §3.1.1).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useCaseCourtDetail } from 'hooks/api/useCaseCourtDetail';
import { useCaseAdvocates } from 'hooks/api/useCaseAdvocates';
import { litigationCaseService } from 'api/services/litigationCaseService';
import { caseCourtDetailService } from 'api/services/caseCourtDetailService';
import { caseAdvocateService } from 'api/services/caseAdvocateService';
import { arbitratorService } from 'api/services/arbitratorService';
import { courtService } from 'api/services/courtService';
import { advocateService } from 'api/services/advocateService';
import type { LitigationCase } from 'api/types/litigationCase.types';
import type { CaseAdvocate } from 'api/types/caseAdvocate.types';

interface Props {
  kase: LitigationCase;
  onMutated: () => void;
}

const dateOnly = (s?: string | null) => (s ? String(s).slice(0, 10) : '');

const CaseDetailsSection: React.FC<Props> = ({ kase, onMutated }) => {
  const isArbitration = kase.caseTypeCode === 'ARBITRATION';
  const { data: court, refetch: refetchCourt } = useCaseCourtDetail(kase.id);
  const { data: advocates, refetch: refetchAdv } = useCaseAdvocates(kase.id);
  const oursAdvocate = useMemo<CaseAdvocate | undefined>(
    () => advocates.find((a) => a.side === 'OURS'), [advocates],
  );

  // ---- Card 1: Case meta ----
  const [meta, setMeta] = useState({ filingDate: '', caseFiledAmount: '', caseSubtype: '', caseBrief: '' });
  const [metaSaving, setMetaSaving] = useState(false);
  useEffect(() => {
    setMeta({
      filingDate: dateOnly(kase.filingDate),
      caseFiledAmount: kase.caseFiledAmount != null ? String(kase.caseFiledAmount) : '',
      caseSubtype: kase.caseSubtype ?? '',
      caseBrief: kase.caseBrief ?? '',
    });
  }, [kase.id, kase.filingDate, kase.caseFiledAmount, kase.caseSubtype, kase.caseBrief]);

  const saveMeta = useCallback(async () => {
    setMetaSaving(true);
    try {
      await litigationCaseService.updateMeta(kase.id, {
        filingDate: meta.filingDate || null,
        caseFiledAmount: meta.caseFiledAmount ? Number(meta.caseFiledAmount) : null,
        caseSubtype: meta.caseSubtype || null,
      });
      if ((kase.caseBrief ?? '') !== meta.caseBrief) {
        await litigationCaseService.updateNotes(kase.id, meta.caseBrief || null);
      }
      notificationService.toast.success('Case details saved.');
      onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to save case details.', 6000);
    } finally { setMetaSaving(false); }
  }, [kase.id, kase.caseBrief, meta, onMutated]);

  // ---- Card 2: Court / Arbitrator ----
  const [cd, setCd] = useState({
    partyId: '', partyLabel: '', location: '', sittingType: '', filingNumber: '', cnrNumber: '',
    jurisdiction: '', judgeOrBench: '', caseDiaryNumber: '', nextHearingDate: '',
  });
  const [cdSaving, setCdSaving] = useState(false);
  useEffect(() => {
    setCd({
      partyId: (isArbitration ? court?.arbitratorId : court?.courtId) ?? '',
      partyLabel: (isArbitration ? court?.arbitratorMasterName : court?.courtMasterName) ?? '',
      location: court?.location ?? '',
      sittingType: court?.sittingType ?? '',
      filingNumber: court?.filingNumber ?? '',
      cnrNumber: court?.cnrNumber ?? '',
      jurisdiction: court?.jurisdiction ?? '',
      judgeOrBench: court?.judgeOrBench ?? '',
      caseDiaryNumber: court?.caseDiaryNumber ?? '',
      nextHearingDate: dateOnly(court?.nextHearingDate),
    });
  }, [court, isArbitration]);

  // Online sittings are virtual — "Place of Arbitration" reads "Arbitration
  // Sitting" (may be a meeting link / platform rather than a physical venue).
  const isOnlineSitting = isArbitration && cd.sittingType === 'ONLINE';
  const locationLabel = !isArbitration ? 'Location'
    : isOnlineSitting ? 'Arbitration Sitting' : 'Place of Arbitration';

  const loadForum = useCallback(async (term: string) => {
    try {
      if (isArbitration) {
        const r = await arbitratorService.getAll({ searchTerm: term || undefined, pageSize: 20 } as any);
        return (r.data ?? []).map((a: any) => ({ value: String(a.id), label: a.arbitratorName ?? a.name ?? a.arbitratorCode }));
      }
      const r = await courtService.getAll({ searchTerm: term || undefined, pageSize: 20 } as any);
      return (r.data ?? []).map((c: any) => ({ value: String(c.id), label: c.courtName ?? c.name ?? c.courtCode }));
    } catch { return []; }
  }, [isArbitration]);

  const saveCourt = useCallback(async () => {
    setCdSaving(true);
    try {
      await caseCourtDetailService.save({
        caseId: kase.id,
        courtType: isArbitration ? 'ARBITRATION' : (court?.courtType ?? null),
        courtId: isArbitration ? null : (cd.partyId || null),
        arbitratorId: isArbitration ? (cd.partyId || null) : null,
        location: cd.location || null,
        sittingType: isArbitration ? (cd.sittingType || null) : null,
        filingNumber: cd.filingNumber || null,
        cnrNumber: isArbitration ? null : (cd.cnrNumber || null),
        jurisdiction: cd.jurisdiction || null,
        judgeOrBench: cd.judgeOrBench || null,
        caseDiaryNumber: cd.caseDiaryNumber || null,
        nextHearingDate: cd.nextHearingDate || null,
        rowVersion: court?.rowVersion ?? null,
      });
      notificationService.toast.success(`${isArbitration ? 'Arbitrator' : 'Court'} detail saved.`);
      await refetchCourt(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to save.', 6000);
    } finally { setCdSaving(false); }
  }, [kase.id, isArbitration, court, cd, refetchCourt, onMutated]);

  // ---- Card 3: Our Advocate ----
  const [adv, setAdv] = useState({ advocateId: '', advocateLabel: '', firmName: '', phone: '' });
  const [advSaving, setAdvSaving] = useState(false);
  useEffect(() => {
    setAdv({
      advocateId: oursAdvocate?.advocateId ?? '',
      advocateLabel: oursAdvocate?.advocateMasterName ?? oursAdvocate?.advocateName ?? '',
      firmName: oursAdvocate?.firmName ?? '',
      phone: oursAdvocate?.phone ?? '',
    });
  }, [oursAdvocate]);

  const loadAdvocates = useCallback(async (term: string) => {
    try {
      const r = await advocateService.getAll({ searchTerm: term || undefined, pageSize: 20 } as any);
      return (r.data ?? []).map((a: any) => ({ value: String(a.id), label: a.advocateName ?? a.name ?? a.advocateCode }));
    } catch { return []; }
  }, []);

  const saveAdvocate = useCallback(async () => {
    if (!adv.advocateId) { notificationService.toast.warning('Pick an advocate from the master.'); return; }
    setAdvSaving(true);
    try {
      if (oursAdvocate) {
        await caseAdvocateService.update(oursAdvocate.id, {
          side: 'OURS', advocateId: adv.advocateId, firmName: adv.firmName || null,
          phone: adv.phone || null, displayOrder: oursAdvocate.displayOrder ?? 100,
          rowVersion: oursAdvocate.rowVersion,
        } as any);
      } else {
        await caseAdvocateService.create({
          caseId: kase.id, side: 'OURS', advocateId: adv.advocateId,
          firmName: adv.firmName || null, phone: adv.phone || null, displayOrder: 0,
        } as any);
      }
      notificationService.toast.success('Our advocate saved.');
      await refetchAdv(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to save advocate.', 6000);
    } finally { setAdvSaving(false); }
  }, [kase.id, adv, oursAdvocate, refetchAdv, onMutated]);

  return (
    <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
      {/* Card 1 — Case Details */}
      <Card title="Case Details" onSave={() => void saveMeta()} saving={metaSaving}>
        <Field label="System Case Number">
          <div style={roStyle}>{kase.caseCode}</div>
        </Field>
        <Field label="Case Date">
          <input type="date" className="form-input" value={meta.filingDate}
            onChange={(e) => setMeta((m) => ({ ...m, filingDate: e.target.value }))} />
        </Field>
        <Field label="Case Amount (₹)">
          <input type="number" min={0} className="form-input" value={meta.caseFiledAmount}
            onChange={(e) => setMeta((m) => ({ ...m, caseFiledAmount: e.target.value }))} />
        </Field>
        <Field label="Subtype">
          <input type="text" className="form-input" value={meta.caseSubtype}
            placeholder="e.g. ARB-§34" onChange={(e) => setMeta((m) => ({ ...m, caseSubtype: e.target.value }))} />
        </Field>
        <Field label="Case Brief" full>
          <textarea className="form-textarea" rows={3} value={meta.caseBrief}
            onChange={(e) => setMeta((m) => ({ ...m, caseBrief: e.target.value }))} />
        </Field>
      </Card>

      {/* Card 2 — Court / Arbitrator */}
      <Card title={isArbitration ? 'Arbitrator' : 'Court'} onSave={() => void saveCourt()} saving={cdSaving}>
        <Field label={isArbitration ? 'Arbitrator Name' : 'Court'}>
          <SyutSelect isAsync loadOptions={loadForum} defaultOptions cacheOptions={false}
            value={cd.partyId} seedOptions={cd.partyId ? [{ value: cd.partyId, label: cd.partyLabel }] : undefined}
            onChange={(v) => setCd((c) => ({ ...c, partyId: (v as string) || '' }))}
            placeholder={isArbitration ? 'Search arbitrators…' : 'Search courts…'} size="sm" />
        </Field>
        {isArbitration && (
          <Field label="Sitting Type">
            <SyutSelect
              options={[{ value: 'OFFLINE', label: 'Offline' }, { value: 'ONLINE', label: 'Online' }]}
              value={cd.sittingType}
              onChange={(v) => setCd((c) => ({ ...c, sittingType: (v as string) || '' }))}
              placeholder="Offline / Online" isClearable size="sm" />
          </Field>
        )}
        <Field label={locationLabel}>
          <input type="text" className="form-input" value={cd.location}
            placeholder={isOnlineSitting ? 'e.g. video-conference / platform' : undefined}
            onChange={(e) => setCd((c) => ({ ...c, location: e.target.value }))} />
        </Field>
        <Field label={isArbitration ? 'Arbitrator Reference Case Number' : 'Court Case Number'}>
          <input type="text" className="form-input" value={cd.filingNumber}
            onChange={(e) => setCd((c) => ({ ...c, filingNumber: e.target.value }))} />
        </Field>
        {!isArbitration && (
          <Field label="CNR Number">
            <input type="text" className="form-input" value={cd.cnrNumber}
              placeholder="eCourts 16-char CNR"
              onChange={(e) => setCd((c) => ({ ...c, cnrNumber: e.target.value }))} />
          </Field>
        )}
        {!isArbitration && (
          <Field label="Judge / Bench">
            <input type="text" className="form-input" value={cd.judgeOrBench}
              onChange={(e) => setCd((c) => ({ ...c, judgeOrBench: e.target.value }))} />
          </Field>
        )}
        <Field label="Jurisdiction">
          <input type="text" className="form-input" value={cd.jurisdiction}
            onChange={(e) => setCd((c) => ({ ...c, jurisdiction: e.target.value }))} />
        </Field>
        <Field label="Case Diary Number">
          <input type="text" className="form-input" value={cd.caseDiaryNumber}
            onChange={(e) => setCd((c) => ({ ...c, caseDiaryNumber: e.target.value }))} />
        </Field>
        <Field label="Next Hearing Date">
          <input type="date" className="form-input" value={cd.nextHearingDate}
            onChange={(e) => setCd((c) => ({ ...c, nextHearingDate: e.target.value }))} />
        </Field>
      </Card>

      {/* Card 3 — Our Advocate */}
      <Card title="Our Advocate" onSave={() => void saveAdvocate()} saving={advSaving}>
        <Field label="Advocate">
          <SyutSelect isAsync loadOptions={loadAdvocates} defaultOptions cacheOptions={false}
            value={adv.advocateId} seedOptions={adv.advocateId ? [{ value: adv.advocateId, label: adv.advocateLabel }] : undefined}
            onChange={(v) => setAdv((a) => ({ ...a, advocateId: (v as string) || '' }))}
            placeholder="Search advocates…" size="sm" />
        </Field>
        <Field label="Firm">
          <input type="text" className="form-input" value={adv.firmName}
            onChange={(e) => setAdv((a) => ({ ...a, firmName: e.target.value }))} />
        </Field>
        <Field label="Phone">
          <input type="text" className="form-input" value={adv.phone}
            onChange={(e) => setAdv((a) => ({ ...a, phone: e.target.value }))} />
        </Field>
      </Card>
    </div>
  );
};

// ---- Layout helpers ----
const Card: React.FC<{ title: string; saving: boolean; onSave: () => void; children: React.ReactNode }> = ({ title, saving, onSave, children }) => (
  <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, background: 'var(--bg-primary)', overflow: 'hidden' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.85rem', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
      <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{title}</span>
      <button type="button" className="btn btn-primary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.78rem' }} disabled={saving} onClick={onSave}>
        <IconSystem.check size={13} style={{ marginRight: '0.3rem' }} />{saving ? 'Saving…' : 'Save'}
      </button>
    </div>
    <div style={{ padding: '0.85rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem 0.85rem' }}>
      {children}
    </div>
  </div>
);

const Field: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className="form-group" style={{ margin: 0, gridColumn: full ? '1 / -1' : undefined }}>
    <label className="form-label" style={{ fontSize: '0.78rem', marginBottom: '0.2rem' }}>{label}</label>
    {children}
  </div>
);

const roStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem', fontSize: '0.85rem', fontWeight: 600,
  background: 'var(--bg-secondary)', borderRadius: 6, border: '1px solid var(--border-color)',
};

export default CaseDetailsSection;
